import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { scarcityNames } from '@sorare/core/src/lib/cards';
import { withFragments } from '@sorare/core/src/lib/gql';
import { sortByRarity } from '@sorare/core/src/lib/scarcity';
import { toCamelCase } from '@sorare/core/src/lib/toCamelCase';

import Captain from 'components/so5/Captain';
import { RuleHelperFnReturnType } from 'components/so5/Rules/types';
import { getScoreModifiers } from 'lib/so5';

import { getCaptainRule_so5Leaderboard } from './__generated__/getCaptainRule.graphql';

const messages = defineMessages({
  title: {
    id: 'Rules.captainRarities.title',
    defaultMessage: `{display, select,
      specific {{rarities} }
      other {}
    }Captain`,
  },
  description: {
    id: 'CaptainRule.captainRarities.description',
    defaultMessage: `{score, select,
            noPoints {Card scores 0 points}
            less {Card score decreased by {scoreModifier, number, percent}}
            more {Card score increased by {scoreModifier, number, percent}}
            other {}
        }`,
  },
  descriptionFtue: {
    id: 'CaptainRule.captainRarities.descriptionFtue',
    defaultMessage:
      'The card you chose as your captain has its score increased by {scoreModifier, number, percent}',
  },
});

const getCaptainRule = withFragments(
  (
    so5Leaderboard: getCaptainRule_so5Leaderboard,
    error: string,
    ftue: boolean
  ): RuleHelperFnReturnType => {
    const { displayedRules, engineConfiguration } = so5Leaderboard;
    const rarities = (displayedRules?.captainRarities || []) as Rarity[];

    const { score, scoreModifier } = getScoreModifiers(
      engineConfiguration?.captain || 0
    );

    const needCaptain = rarities.length > 0;
    if (!needCaptain) {
      return [];
    }

    const playableRarities = (
      [
        Rarity.common,
        Rarity.limited,
        Rarity.rare,
        Rarity.super_rare,
        Rarity.unique,
      ] as const
    ).filter(rarity => {
      const limit = displayedRules?.rarityLimits?.[toCamelCase(rarity)];

      return limit?.min || limit?.max;
    });

    const canCaptainEveryPlayableRarity = playableRarities.every(rarity =>
      rarities.includes(rarity)
    );

    return {
      id: 'captainRarities',
      label: (
        <FormattedMessage
          {...messages.title}
          values={{
            display: canCaptainEveryPlayableRarity ? 'any' : 'specific',
            rarities: sortByRarity(rarities)
              .reverse()
              .map(rarity => scarcityNames[rarity])
              .join(', '),
          }}
        />
      ),
      description: (
        <FormattedMessage
          {...(ftue ? messages.descriptionFtue : messages.description)}
          values={{
            score,
            scoreModifier,
          }}
        />
      ),
      icon: <Captain active size={2} />,
      error,
    };
  },
  {
    so5Leaderboard: gql`
      fragment getCaptainRule_so5Leaderboard on So5Leaderboard {
        slug
        displayedRules {
          id
          captainRarities
          rarityLimits {
            common {
              min
              max
            }
            limited {
              min
              max
            }
            rare {
              min
              max
            }
            superRare {
              min
              max
            }
            unique {
              min
              max
            }
          }
        }
        engineConfiguration {
          captain
        }
      }
    ` as TypedDocumentNode<getCaptainRule_so5Leaderboard>,
  }
);

export default getCaptainRule;
